import { z } from 'zod';
import { apiFor } from '@/api/client';
import { toCsv } from './csv';
import type { SurveyStrings } from './i18n';
import { fmtDate } from './utils';

/*
 * Winners & prizes — the weekly €500 lottery (passport mg32): one winner per
 * week, drawn by the mini-games service. The client completes each winner with
 * the store and the display name and approves them, which PUBLISHES both to
 * every player of the campaign — they cannot be edited afterwards.
 *
 * Live endpoints, not part of the report payload (personal data, and an action
 * of its own):
 *   GET  <base>/reports/:id/winners                      → { data: { rows } }
 *   POST <base>/reports/:id/winners/:round/approve       { storeAddress, displayName }
 * The portal passes both through to the tenant's lottery admin API (backend:
 * lib/service/v1/systemeUWinners.js). One row per weekly round, keyed by the
 * service's round key ("p1-r2").
 */

/*
 * Shows the "Winners & prizes" link in the dashboard header. Set to false to
 * hide it again — the page keeps its route either way, so it still opens by URL.
 */
export const WINNERS_LINK_VISIBLE = true;

const winnerSchema = z.object({
  /** The service's round key, "p1-r2" — also what approve is addressed to. */
  id: z.union([z.number(), z.string()]),
  round: z.string().optional(),
  /** Campaign week the draw belongs to; null for a round outside the campaign's weeks (QA test rounds). */
  week: z.number().int().positive().nullable(),
  /** When the winner was drawn, campaign wall time. */
  drawnAt: z.string().nullable(),
  /** The member's Système U identifier — what the operator checks the person against. */
  cardNumber: z.string().nullable(),
  storeAddress: z.string().nullable(),
  displayName: z.string().nullable(),
  /** The prize, a credit in euros (500). */
  prizeEur: z.number(),
  /** no_winner = nobody had a ticket that week, so nothing was drawn. */
  status: z.enum(['pending', 'approved', 'no_winner']),
  approvedAt: z.string().nullable().optional(),
  /** Tickets in the draw. */
  tickets: z.number().nullable().optional(),
});

export const winnersEnvelope = z.object({ data: z.object({ rows: z.array(winnerSchema) }) });

export type SurveyWinner = z.infer<typeof winnerSchema>;

export async function getSurveyWinners(reportId: string, baseUrl: string): Promise<SurveyWinner[]> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}/winners`).json();
  return winnersEnvelope.parse(raw).data.rows;
}

export async function approveSurveyWinner(
  reportId: string,
  baseUrl: string,
  winnerId: SurveyWinner['id'],
  body: { storeAddress: string; displayName: string },
): Promise<void> {
  await apiFor(baseUrl).post(`reports/${reportId}/winners/${winnerId}/approve`, { json: body });
}

/* ---- dialog validation ---------------------------------------------------- */

export const STORE_MAX = 200;

/**
 * "First name and the initial of the surname" (design hint): "Camille M.",
 * "Jean-Pierre D", "Anne-Sophie de L.". Letters in any script, spaces, hyphens
 * and apostrophes, ending in a capital initial with an optional dot. The rule
 * keeps full surnames out of the display name.
 */
export const DISPLAY_NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M} '’-]*\s\p{Lu}\.?$/u;

/**
 * The lottery service's own character rule for both published texts: letters in
 * any script, digits, spaces and . , ; : ! ? ° ' ’ ( ) / - — no "·", no markup.
 */
export const LOTTERY_TEXT_RE = /^[\p{L}\p{N} .,;:!?°'’()/-]+$/u;

/** Why a field was refused; the dialog words it in the page's language (`approvalError` in i18n.ts). */
export type ApprovalErrorCode = 'storeRequired' | 'storeTooLong' | 'storeChars' | 'nameRequired' | 'nameFormat';

export function validateApproval(
  store: string,
  name: string,
): { storeAddress: string; displayName: string } | { field: 'store' | 'name'; code: ApprovalErrorCode } {
  // NFC: an "é" typed as e + accent must reach the service as one letter.
  const storeAddress = store.normalize('NFC').replace(/\s+/g, ' ').trim();
  const displayName = name.normalize('NFC').replace(/\s+/g, ' ').trim();
  if (!storeAddress) return { field: 'store', code: 'storeRequired' };
  if (storeAddress.length > STORE_MAX) return { field: 'store', code: 'storeTooLong' };
  if (!LOTTERY_TEXT_RE.test(storeAddress)) return { field: 'store', code: 'storeChars' };
  if (!displayName) return { field: 'name', code: 'nameRequired' };
  if (!DISPLAY_NAME_RE.test(displayName)) return { field: 'name', code: 'nameFormat' };
  return { storeAddress, displayName };
}

/* ---- display & export ----------------------------------------------------- */

/** "2971045833162204" → "2971 0458 3316 2204"; anything that is not all digits is left as it is. */
export function fmtCardNumber(raw: string | null): string {
  if (!raw) return '—';
  const digits = raw.replace(/\s+/g, '');
  return /^\d{8,}$/.test(digits) ? digits.replace(/(\d{4})(?=\d)/g, '$1 ') : raw;
}

/** Round keys are `p<season>-r<week of the season>`: "p2-r1" is Season 2's first weekly draw. */
const ROUND_KEY = /^p(\d+)-r(\d+)$/;

/**
 * "S2 · WK 1" for a draw inside the campaign (the backend gives it a week),
 * read from its round key; "WK 3" when the key has another shape; the key
 * itself for a round outside the campaign (a pre-launch test draw).
 */
export function roundLabel(w: SurveyWinner, t: SurveyStrings): string {
  if (w.week == null) return w.round ?? String(w.id);
  const m = ROUND_KEY.exec(w.round ?? '');
  return m ? t.seasonWk(Number(m[1]), Number(m[2])) : t.wk(w.week);
}

/** A drawn winner still waiting for the store and the name. */
export const isMissing = (w: SurveyWinner) => w.status === 'pending' && (!w.storeAddress || !w.displayName);

export function winnersCsv(rows: SurveyWinner[], t: SurveyStrings): string {
  const h = t.csvWinners;
  return toCsv(
    [h.week, h.drawDate, h.store, h.name, h.card, h.prize, h.status],
    rows.map((w) => [roundLabel(w, t), fmtDate(w.drawnAt), w.storeAddress, w.displayName, w.cardNumber, w.prizeEur, t.winnerStatus[w.status]]),
  );
}

/** The design's four draws — DEV ONLY, when the winners endpoint does not answer. Keys and shapes as the backend sends them. */
export const SU_WINNERS_PREVIEW: SurveyWinner[] = [
  {
    id: 'p1-r4',
    round: 'p1-r4',
    week: 4,
    drawnAt: '2026-11-02',
    cardNumber: '2971045833162204',
    storeAddress: null,
    displayName: null,
    prizeEur: 500,
    status: 'pending',
    tickets: 160120,
  },
  {
    id: 'p1-r3',
    round: 'p1-r3',
    week: 3,
    drawnAt: '2026-10-26',
    cardNumber: '2971023990871462',
    storeAddress: 'U Express, 9 place du Marché, 49000 Angers',
    displayName: 'Sophie L.',
    prizeEur: 500,
    status: 'approved',
    tickets: 172950,
  },
  {
    id: 'p1-r2',
    round: 'p1-r2',
    week: 2,
    drawnAt: '2026-10-19',
    cardNumber: '2971039144280735',
    storeAddress: 'Hyper U, 18 boulevard de l’Europe, 85000 La Roche-sur-Yon',
    displayName: 'Léa M.',
    prizeEur: 500,
    status: 'approved',
    tickets: 196410,
  },
  {
    id: 'p1-r1',
    round: 'p1-r1',
    week: 1,
    drawnAt: '2026-10-12',
    cardNumber: '2971012458693371',
    storeAddress: 'U Express, 23 rue Nationale, 37000 Tours',
    displayName: 'Chloé L.',
    prizeEur: 500,
    status: 'approved',
    tickets: 245600,
  },
];
